import React, { useState } from "react";
import privateAxios from "../../utils/axiosPrivate";
import { FaCheck, FaInfoCircle, FaEye, FaEyeSlash } from "react-icons/fa";
import {
  accSoftwareTab,
  auPhNumber,
  auPhNumberTag,
  buttonName,
  commonErrMsg,
  mail,
  monthlyLending,
  nzPhNumber,
  nzPhNumberTag,
  nzText1,
  passwordtext
} from "../../locales/en";
import Style from "./../profile/profile.module.scss";
import "./password.scss";
import { getProfileChangePassUrl } from "../../utils/constants/apiEndPoints";
import { sanitizeInput } from "../../utils/utils";
import { Link } from "react-router-dom";
import {
  DIGIT_REGEX,
  ESCAPE_REGEX,
  MAILTO_INFO_EMAIL,
  UPPERCASE_REGEX
} from "../../utils/constants/appConstants";

const Password = () => {
  const initialFormData = {
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  };
  const [formData, setFormData] = useState(initialFormData);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordValid, setPasswordValid] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push(passwordtext?.passwordError1);
    }
    if (!DIGIT_REGEX.test(password)) {
      errors.push(passwordtext?.passwordError2);
    }
    if (!UPPERCASE_REGEX.test(password)) {
      errors.push(passwordtext?.passwordError3);
    }
    if (!ESCAPE_REGEX.test(password)) {
      errors.push(passwordtext?.passwordError4);
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);

    setFormData((prevState) => ({ ...prevState, [name]: sanitizedValue }));

    if (name === "newPassword") {
      const errors = validatePassword(sanitizedValue);
      setPasswordErrors(errors);
      setPasswordValid(errors.length === 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const oldPassword = sanitizeInput(formData.oldPassword);
    const newPassword = sanitizeInput(formData.newPassword);
    const confirmPassword = sanitizeInput(formData.confirmPassword);

    if (!oldPassword.trim()) {
      setError(passwordtext?.passwordtext);
      setSuccessMessage("");
      return;
    }

    if (!newPassword.trim()) {
      setError(passwordtext?.newPassError);
      setSuccessMessage("");
      return;
    }

    if (!confirmPassword.trim()) {
      setError(passwordtext?.confirmPassError);
      setSuccessMessage("");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(passwordtext?.newAndConfirmPassError);
      setSuccessMessage("");
      return;
    }

    const userData = { oldPassword, newPassword, confirmPassword };

    try {
      const response = await privateAxios.put(
        getProfileChangePassUrl,
        userData
      );

      if (response.status === 200) {
        setError("");
        setPasswordErrors([]);
        setPasswordValid(true);
        setSuccessMessage(response.data.message);
      }
    } catch (error) {
      console.error(passwordtext?.errorFromChangePass, error);
      if (error.response) {
        const { status, data } = error.response;
        if (status === 400 && Array.isArray(data.errors)) {
          const errorMessages = data.errors
            .map((errorObj) => Object.values(errorObj).flat().join(", "))
            .join(", ");

          setError(errorMessages);
        } else if (status === 500 || status === 501) {
          setError(data.message || commonErrMsg.item2);
        } else {
          setError(commonErrMsg.item4);
        }
      } else {
        setError(passwordtext?.unexpectedError3);
      }
    }

    setFormData({
      oldPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
    setPasswordErrors([]);
    setPasswordValid(false);
  };

  const togglePasswordVisibility = (type) => {
    if (type === "newPassword") {
      setShowNewPassword(!showNewPassword);
    }
  };

  return (
    <div className="profile-form">
      <form onSubmit={handleSubmit}>
        {error && <div className="alert alert-danger">{error}</div>}
        {successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}
        <div
          className={`${Style.inputField} form-group d-flex flex-wrap align-items-center mb-3`}
        >
          <label htmlFor="oldPassword" className="col-12 col-md-5 pt-1 pb-1">
            {passwordtext?.oldPassTitle}
          </label>
          <input
            type="password"
            id="oldPassword"
            name="oldPassword"
            placeholder="Old Password"
            className="col-12 col-md-7"
            value={formData.oldPassword}
            onChange={handleChange}
          />
        </div>
        <div
          className={`${Style.inputField} form-group d-flex flex-wrap align-items-center mb-3`}
        >
          <label htmlFor="newPassword" className="col-12 col-md-5 pt-1 pb-1">
            {passwordtext?.newPassTitle}
          </label>
          <div className="col-12 col-md-7 pt-1 pb-1 account_new_pass">
            <div className="account_new_pass_sub">
              <input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                placeholder="New Password"
                className="col-12 col-md-7"
                value={formData.newPassword}
                onChange={handleChange}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility("newPassword")}
                className="eyeBtn"
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <div className="col-12 col-md-12 ">
              {passwordErrors.length > 0
                ? passwordErrors.map((err, index) => (
                    <small key={index} className="mt-1 d-block text-danger">
                      <FaInfoCircle /> {err}
                    </small>
                  ))
                : passwordValid && (
                    <small className="text-success">
                      <FaCheck /> {passwordtext?.okPassword}
                    </small>
                  )}
            </div>
          </div>
        </div>

        <div
          className={`${Style.inputField} form-group d-flex flex-wrap align-items-center mb-3`}
        >
          <label
            htmlFor="confirmPassword"
            className="col-12 col-md-5 pt-1 pb-1"
          >
            {passwordtext?.confirmPass}
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            placeholder="New Password Confirmation"
            className="col-12 col-md-7"
            value={formData.confirmPassword}
            onChange={handleChange}
          />
        </div>

        <div className="row">
          <div className="col-md-5" />
          <div
            className={`${Style.inputField} form-group d-flex flex-wrap col-12 col-md-7 p-0`}
          >
            <button
              type="submit"
              className={`${Style.updateBtn} btn btn-primary`}
              disabled={!passwordValid}
            >
              {buttonName.update}
            </button>
          </div>
        </div>
      </form>
      <div
        className={`${Style.inputField} ${Style.inputFieldFullwidth} form-group d-flex flex-wrap align-items-center my-4`}
      >
        <p>
          {`${accSoftwareTab?.haveQs} `}
          <Link to={MAILTO_INFO_EMAIL}>{mail}</Link>
          {` ${accSoftwareTab?.orCall}`}
          <br />
          {monthlyLending.au}
          <Link to={`tel:${auPhNumberTag}`}>{auPhNumber}</Link>
          {` ${nzText1} `}
          <Link to={`tel:${nzPhNumberTag}`}>{nzPhNumber}</Link>
        </p>
      </div>
    </div>
  );
};

export default Password;
